<template>
  <!-- Render as router-link -->
  <router-link
    v-if="tagCompute === 'router-link'"
    :id="id"
    :to="to"
    :disabled="disabled || loading || null"
    class="button relative inline-flex justify-center uppercase font-medium text-s tracking-[1px] leading-normal"
    :class="[variantClass, sizeClass, { 'button--loading': loading }]"
    :type="type"
    :aria-disabled="disabled || loading"
    :aria-label="ariaLabel"
    :aria-busy="loading"
    :aria-describedby="loading ? 'button-loading-description' : undefined"
    @click="clickEmitter"
    @focus="handleFocus"
    @keydown.enter="handleKeydown"
    @keydown.space="handleKeydown"
  >
    <span v-if="loading" id="button-loading-description" class="sr-only">Loading</span>
    <Spinner v-if="loading" aria-hidden="true" />
    <BackIcon v-if="variant === 'back'" class="mr-8" aria-hidden="true" />
    <span :class="{ invisible: loading }"><slot name="left-icon"></slot></span>
    <span :class="{ invisible: loading }">
      {{ value }}
    </span>
    <slot></slot>
    <Arrow v-if="variant === 'arrow'" aria-hidden="true" />
  </router-link>

  <!-- Render as anchor -->
  <a
    v-else-if="tagCompute === 'a'"
    :id="id"
    :href="hrefCompute"
    :disabled="disabled || loading || null"
    :target="target"
    class="button relative inline-flex justify-center uppercase font-medium text-s tracking-[1px] leading-normal"
    :class="[variantClass, sizeClass, { 'button--loading': loading }]"
    :type="type"
    :aria-disabled="disabled || loading"
    :aria-label="ariaLabel"
    :aria-busy="loading"
    :aria-describedby="loading ? 'button-loading-description' : undefined"
    @click="clickEmitter"
    @focus="handleFocus"
    @keydown.enter="handleKeydown"
    @keydown.space="handleKeydown"
  >
    <span v-if="loading" id="button-loading-description" class="sr-only">Loading</span>
    <Spinner v-if="loading" aria-hidden="true" />
    <BackIcon v-if="variant === 'back'" class="mr-8" aria-hidden="true" />
    <span :class="{ invisible: loading }"><slot name="left-icon"></slot></span>
    <span :class="{ invisible: loading }">
      {{ value }}
    </span>
    <slot></slot>
    <Arrow v-if="variant === 'arrow'" aria-hidden="true" />
  </a>

  <!-- Render as button -->
  <button
    v-else
    :id="id"
    :disabled="disabled || loading || null"
    class="button relative inline-flex justify-center uppercase font-medium text-s tracking-[1px] leading-normal"
    :class="[variantClass, sizeClass, { 'button--loading': loading }]"
    :type="nativeButtonType"
    :aria-disabled="disabled || loading"
    :aria-label="ariaLabel"
    :aria-busy="loading"
    :aria-describedby="loading ? 'button-loading-description' : undefined"
    :value="value"
    @click="clickEmitter"
    @focus="handleFocus"
    @keydown.enter="handleKeydown"
    @keydown.space="handleKeydown"
  >
    <span v-if="loading" id="button-loading-description" class="sr-only">Loading</span>
    <Spinner v-if="loading" aria-hidden="true" />
    <BackIcon v-if="variant === 'back'" class="mr-8" aria-hidden="true" />
    <span :class="{ invisible: loading }"><slot name="left-icon"></slot></span>
    <span :class="{ invisible: loading }">
      {{ value }}
    </span>
    <slot></slot>
    <Arrow v-if="variant === 'arrow'" aria-hidden="true" />
  </button>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import Spinner from '../spinner/Spinner.vue';
import Arrow from '../../icons/ArrowPointer.vue';
import BackIcon from '../../icons/BackIcon.vue';

export type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonVariant = 'primary' | 'secondary' | 'outlined' | 'text' | 'arrow' | 'back';
export type ButtonType = 'button' | 'submit' | 'reset' | '';
export type ButtonTarget = '_blank' | '_self' | '_parent' | '_top' | 'framename' | '';

export default defineComponent({
  name: 'Button',
  components: {
    Arrow,
    Spinner,
    BackIcon,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    size: {
      type: String as PropType<ButtonSize>,
      default: 'small',
      validator: (value: string) => ['small', 'medium', 'large'].includes(value),
    },
    target: {
      type: String as PropType<ButtonTarget>,
      default: '',
    },
    type: {
      type: String as PropType<ButtonType>,
      default: '',
    },
    variant: {
      type: String as PropType<ButtonVariant>,
      default: 'primary',
      validator: (value: string) => ['primary', 'secondary', 'outlined', 'text', 'arrow', 'back'].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    href: {
      type: String,
      default: '',
    },
    preventDefault: {
      type: Boolean,
      default: false,
    },
    to: {
      // Accept either a string path or vue-router route location (string | RouteLocationRaw)
      // RouteLocationRaw can include: name, path, params, query, hash
      type: [String, Object] as PropType<RouteLocationRaw>,
      default: '',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['click', 'focus'],
  computed: {
    hrefCompute(): string | undefined {
      if (this.target && !this.href) return '#';
      return this.href || undefined;
    },
    tagCompute(): string {
      if (this.href || this.target) return 'a';
      if (this.to) {
        return 'router-link';
      }
      return 'button';
    },
    variantClass(): string {
      const variantClasses: Record<ButtonVariant, string> = {
        primary: 'button-primary',
        secondary: 'button-secondary',
        outlined: 'button-outlined',
        text: 'button-text',
        arrow: 'button-arrow',
        back: 'button-back',
      };
      return variantClasses[this.variant];
    },
    sizeClass(): string {
      const sizeClasses: Record<ButtonSize, string> = {
        small: 'button-small',
        medium: 'button-medium',
        large: 'button-large',
      };
      return sizeClasses[this.size];
    },
    nativeButtonType(): 'button' | 'submit' | 'reset' {
      return this.type === '' ? 'button' : (this.type as 'button' | 'submit' | 'reset');
    },
  },
  methods: {
    clickEmitter(e: Event): void {
      if (this.preventDefault) e.preventDefault();
      this.$emit('click', e);
    },
    handleFocus(e: FocusEvent): void {
      this.$emit('focus', e);
    },
    handleKeydown(e: KeyboardEvent): void {
      // this method mimics the behavior of the button when the user presses the Enter or Space key
      // this is necessary because sometimes the button is rendered as a link
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.clickEmitter(e);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.button {
  align-items: center;
  font-family: 'Graphik Web', Helvetica, Arial, sans-serif;
  text-align: center;
  border-radius: theme('borderRadius.md');
  transition: all 100ms linear;
  outline: none;

  &:active {
    transform: scale(97%);
    box-shadow: 0px 0px 0px;
    transition: all 100ms linear;
  }

  &:focus-visible::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: calc(100% + 4px);
    height: calc(100% + 4px);
    border: 2px dotted theme('colors.brand.contrast');
    margin: -2px;
    border-radius: theme('borderRadius.md');
  }

  & .animate-spin {
    position: absolute;
  }

  &.button--loading {
    &,
    &:hover,
    &:focus-visible,
    &[disabled] {
      color: transparent;
      pointer-events: none;
    }
  }

  &[disabled] {
    cursor: not-allowed;
  }
}

.button-small {
  padding: 0.34375rem 0.5rem;
}

.button-medium {
  padding: 0.594rem 1rem;
}

.button-large {
  padding: 1.125rem 1.5rem;
  line-height: 12px;
}

.button-primary {
  color: theme('colors.foreground');

  &:hover,
  &:focus-visible {
    background-color: theme('colors.brand.contrast');
    color: theme('colors.foreground');
  }

  &,
  &.button--loading {
    background-color: theme('colors.primary.DEFAULT');
    box-shadow: theme('boxShadow.sm');
  }

  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: none;
  }

  svg path {
    fill: theme('colors.foreground');
  }
}

.button-secondary {
  color: theme('colors.primary.DEFAULT');
  border-color: theme('colors.grey.divider.contrast');
  border-style: solid;
  border-width: 0.75px;

  &:hover,
  &:focus-visible {
    color: theme('colors.white');
    border-color: theme('colors.brand.contrast');
    background-color: theme('colors.brand.contrast');
  }

  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: transparent;
    box-shadow: none;
  }

  &,
  &.button--loading {
    background-color: theme('colors.foreground');
    box-shadow: theme('boxShadow.sm');
  }

  svg path {
    fill: theme('colors.brand.contrast');
  }
}

.button-outlined {
  border: theme('borderWidth.1') solid theme('colors.grey.divider.contrast');
  background-color: theme('colors.foreground');
  border-radius: theme('borderRadius.md');
  color: theme('colors.primary.DEFAULT');

  &:hover,
  &:focus-visible {
    border-color: theme('colors.brand.contrast');
    color: theme('colors.brand.contrast');

    svg: {
      color: theme('colors.brand.contrast');
    }
  }

  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: none;
  }

  &,
  &.button--loading {
    border: theme('borderWidth.1') solid theme('colors.grey.divider.contrast');
    background-color: theme('colors.foreground');
  }
}

.button-text {
  color: theme('colors.primary.DEFAULT');

  &:hover,
  &:hover svg path {
    color: theme('colors.brand.contrast');
    fill: theme('colors.brand.contrast');
  }

  &:not([disabled]),
  &.button--loading[disabled] {
    background-color: theme('colors.transparent');
    border: theme('borderWidth.1') solid transparent;
    padding-left: 0;
    padding-right: 0;
  }

  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: none;
    border: theme('borderWidth.1') solid transparent;
  }

  svg path {
    fill: theme('colors.primary.DEFAULT');
  }
}

.button-arrow {
  color: theme('colors.primary.DEFAULT');

  &:hover {
    color: theme('colors.brand.contrast');

    .icon-arrow {
      transform: translateX(4px);
      transition: all 200ms linear;

      path: {
        fill: theme('colors.brand.contrast');
      }
    }
  }

  &[disabled] {
    color: theme('colors.grey.text.subdued');
    fill: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: none;
  }

  &,
  &.button--loading {
    background-color: theme('colors.transparent');
    border: theme('borderWidth.1') solid transparent;
    padding-left: 0;
    padding-right: 0;
  }

  .icon-arrow {
    margin-left: 8px;
    transition: all 200ms linear;
  }

  svg path {
    fill: theme('colors.primary.DEFAULT');
  }
}

.button-back {
  color: theme('colors.primary.DEFAULT');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  line-height: 1.5;
  text-transform: none;
  padding: 8px;

  &:hover,
  &:hover svg path {
    color: theme('colors.brand.contrast');
    fill: theme('colors.brand.contrast');
  }

  &:not([disabled]),
  &.button--loading[disabled] {
    background-color: theme('colors.transparent');
    border: theme('borderWidth.1') solid transparent;
    padding-left: 0;
    padding-right: 0;
  }

  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: none;
    border: theme('borderWidth.1') solid transparent;
  }

  svg path {
    fill: theme('colors.primary.DEFAULT');
  }
}
</style>
